/**
 * Bilan Service
 *
 * Handles all accounting statement (bilan comptable) API calls.
 */

import api from './api';
import type {
  ApiResponse,
  AccountingStatement,
  AccountingStatementSummary,
  AccountingStatementComparison,
  AccountingStatementLimitResponse,
  AccountingStatementFilters,
  CreateAccountingStatementRequest,
  UpdateAccountingStatementRequest,
  PaginatedResponse,
} from '@budgira/shared';

export const bilanService = {
  /**
   * Get all statements with filters and pagination
   */
  async list(
    filters: AccountingStatementFilters = {},
    page: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedResponse<AccountingStatementSummary>> {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());

    if (filters.year) {
      params.set('year', filters.year.toString());
    }
    if (filters.accountId) {
      params.set('accountId', filters.accountId);
    }
    if (filters.search) {
      params.set('search', filters.search);
    }

    const response = await api.get<ApiResponse<PaginatedResponse<AccountingStatementSummary>>>(
      `/accounting-statements?${params.toString()}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch statements');
    }
    return response.data.data;
  },

  /**
   * Get a specific statement by ID
   */
  async getById(id: string): Promise<AccountingStatement> {
    const response = await api.get<ApiResponse<AccountingStatement>>(
      `/accounting-statements/${id}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch statement');
    }
    return response.data.data;
  },

  /**
   * Create a new statement
   */
  async create(data: CreateAccountingStatementRequest): Promise<AccountingStatement> {
    const response = await api.post<ApiResponse<AccountingStatement>>(
      '/accounting-statements',
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create statement');
    }
    return response.data.data;
  },

  /**
   * Update an existing statement
   */
  async update(
    id: string,
    data: UpdateAccountingStatementRequest
  ): Promise<AccountingStatement> {
    const response = await api.put<ApiResponse<AccountingStatement>>(
      `/accounting-statements/${id}`,
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update statement');
    }
    return response.data.data;
  },

  /**
   * Delete a statement
   */
  async delete(id: string): Promise<void> {
    const response = await api.delete<ApiResponse<null>>(`/accounting-statements/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete statement');
    }
  },

  /**
   * Recalculate a statement with current operations
   */
  async recalculate(id: string): Promise<AccountingStatement> {
    const response = await api.post<ApiResponse<AccountingStatement>>(
      `/accounting-statements/${id}/recalculate`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to recalculate statement');
    }
    return response.data.data;
  },

  /**
   * Compare two statements
   */
  async compare(id1: string, id2: string): Promise<AccountingStatementComparison> {
    const response = await api.get<ApiResponse<AccountingStatementComparison>>(
      `/accounting-statements/compare?id1=${id1}&id2=${id2}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to compare statements');
    }
    return response.data.data;
  },

  /**
   * Check user's statement limit
   */
  async checkLimit(): Promise<AccountingStatementLimitResponse> {
    const response = await api.get<ApiResponse<AccountingStatementLimitResponse>>(
      '/accounting-statements/limit'
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to check limit');
    }
    return response.data.data;
  },

  /**
   * Export a statement as PDF (returns blob)
   */
  async exportPdf(id: string): Promise<Blob> {
    const response = await api.get(`/accounting-statements/${id}/export/pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  /**
   * Export comparison as PDF (returns blob)
   */
  async exportComparisonPdf(id1: string, id2: string): Promise<Blob> {
    const response = await api.get(
      `/accounting-statements/compare/export/pdf?id1=${id1}&id2=${id2}`,
      {
        responseType: 'blob',
      }
    );
    return response.data as Blob;
  },
};

export default bilanService;
